import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { IconPrescription } from "@tabler/icons-react";
import { MButton } from "@/components/ui/atoms/MButtons";
import { PrescriptionVitalSuggestion, VitalsParameter } from "@/types/vitals";
import styles from "./PrescriptionVitalCard.module.css";

interface PrescriptionVitalCardProps {
  suggestion: PrescriptionVitalSuggestion;
  /**
   * Possessive form used in "Add it to ___ readings?" — "your" for the signed-in
   * member, "Priya's" for someone else. Already possessive: the copy does not
   * append an apostrophe.
   */
  memberPossessive: string;
  onAdd: () => void;
  onIgnore: () => void;
  loading?: boolean;
}

function formatBpValue(values: Record<string, any>): string {
  return `${values.systolic}/${values.diastolic}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  try {
    return format(parseISO(dateStr), "d MMM");
  } catch {
    return dateStr;
  }
}

const PrescriptionVitalCard: React.FC<PrescriptionVitalCardProps> = ({
  suggestion,
  memberPossessive,
  onAdd,
  onIgnore,
  loading,
}) => {
  const { t } = useTranslation();
  const date = formatDate(suggestion.dateOfVisit);
  const doctor = suggestion.doctorName;

  const bodyKey =
    suggestion.parameter === VitalsParameter.BP
      ? doctor
        ? "vitalsTracker.prescriptionVital.bpBody"
        : "vitalsTracker.prescriptionVital.bpBodyNoDoctor"
      : doctor
      ? "vitalsTracker.prescriptionVital.weightBody"
      : "vitalsTracker.prescriptionVital.weightBodyNoDoctor";

  const bodyValues =
    suggestion.parameter === VitalsParameter.BP
      ? {
          value: formatBpValue(suggestion.values),
          doctor: doctor ?? "",
          date,
          name: memberPossessive,
        }
      : {
          value: suggestion.values.value,
          unit: suggestion.values.unit ?? "kg",
          doctor: doctor ?? "",
          date,
          name: memberPossessive,
        };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.rxBadge}>
          <IconPrescription size={14} color="#4c189a" stroke={1.5} />
        </div>
        <p className={styles.title}>
          {t("vitalsTracker.prescriptionVital.title")}
        </p>
      </div>

      <p className={styles.body}>
        <Trans
          i18nKey={bodyKey}
          values={bodyValues}
          components={{ bold: <strong /> }}
        />
      </p>

      <div className={styles.actions}>
        <MButton
          variant="outline"
          size="sm"
          text={t("vitalsTracker.prescriptionVital.ignore")}
          onClick={onIgnore}
          disabled={loading}
          className={styles.actionBtn}
        />
        <MButton
          variant="filled"
          size="sm"
          text={t("vitalsTracker.prescriptionVital.addReading")}
          onClick={onAdd}
          disabled={loading}
          className={styles.actionBtn}
        />
      </div>
    </div>
  );
};

export default PrescriptionVitalCard;
